import React from "react"
import PropTypes from "prop-types"
import { Button, Typography, Spin, Flex } from "antd"
import { MessageOutlined, CloseOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"
import styles from "../../style.module.css"

dayjs.extend(relativeTime)

const { Text } = Typography

function parseUtc(isoString) {
  const normalized = isoString.endsWith("Z") || /[+-]\d{2}:\d{2}$/.test(isoString)
    ? isoString
    : isoString + "Z"
  return dayjs(normalized)
}

function formatSessionTime(isoString) {
  if (!isoString) return ""
  const date = parseUtc(isoString)
  const now = dayjs()
  const diffHours = now.diff(date, "hour")

  if (diffHours < 24) return date.fromNow()
  if (diffHours < 48) return "Yesterday"
  return date.format("MMM D")
}

function truncatePreview(text, maxLength = 60) {
  if (!text) return ""
  return text.length > maxLength ? text.slice(0, maxLength).trimEnd() + "…" : text
}

export default function ChatSessionsSidebar({
  sessionsList,
  sessionsLoading,
  sessionsError,
  hasMoreSessions,
  currentSessionId,
  onSelectSession,
  onLoadMore,
  onRetry,
  onClose,
}) {
  const { t } = useTranslation()

  function renderContent() {
    if (sessionsError) {
      return (
        <Flex vertical align="center" gap={4} className={styles.sessionsError}>
          <Text type="secondary" className={styles.textSm}>{t("ai_dashboard.sessions_load_error")}</Text>
          <Button size="small" type="link" onClick={onRetry}>{t("ai_dashboard.retry")}</Button>
        </Flex>
      )
    }
    if (sessionsLoading && sessionsList.length === 0) {
      return (
        <Flex justify="center" align="center" className={styles.sessionsSpinner}>
          <Spin size="small" />
        </Flex>
      )
    }
    if (sessionsList.length === 0) {
      return (
        <Flex vertical align="center" gap={8} className={styles.sessionsEmpty}>
          <MessageOutlined className={styles.sessionsEmptyIcon} />
          <Text type="secondary" className={styles.textSm}>{t("ai_dashboard.no_past_sessions")}</Text>
        </Flex>
      )
    }
    return (
      <>
        {sessionsList.map((session) => {
          const preview = truncatePreview(session.preview) || t("ai_dashboard.new_conversation")
          const isActive = session.session_id === currentSessionId
          return (
            <button
              key={session.session_id}
              type="button"
              className={`${styles.sessionItem} ${isActive ? styles.sessionItemActive : ""}`}
              onClick={() => onSelectSession(session.session_id)}
            >
              <Flex align="flex-start" gap={8}>
                <Text className={styles.sessionPreview} ellipsis={{ rows: 2 }}>{preview}</Text>
                {session.message_count > 0 && (
                  <span className={styles.sessionBadge}>{session.message_count}</span>
                )}
              </Flex>
              <Text type="secondary" className={styles.sessionTime}>
                {formatSessionTime(session.last_message_at || session.started_at)}
              </Text>
            </button>
          )
        })}

        {hasMoreSessions && (
          <Button
            type="link"
            size="small"
            loading={sessionsLoading}
            onClick={onLoadMore}
            className={styles.loadMoreBtn}
          >
            {t("ai_dashboard.load_more_sessions")}
          </Button>
        )}
      </>
    )
  }

  return (
    <Flex vertical className={styles.sessionsSidebar}>
      <Flex align="center" className={styles.sidebarHeader}>
        <Text className={styles.sidebarTitle}>{t("ai_dashboard.chat_history")}</Text>
        <Button
          type="text"
          size="small"
          icon={<CloseOutlined />}
          className={styles.sidebarCloseBtn}
          onClick={onClose}
        />
      </Flex>
      <Flex vertical className={styles.sessionsList}>
        {renderContent()}
      </Flex>
    </Flex>
  )
}

ChatSessionsSidebar.propTypes = {
  sessionsList: PropTypes.array.isRequired,
  sessionsLoading: PropTypes.bool.isRequired,
  sessionsError: PropTypes.bool.isRequired,
  hasMoreSessions: PropTypes.bool.isRequired,
  currentSessionId: PropTypes.string,
  onSelectSession: PropTypes.func.isRequired,
  onLoadMore: PropTypes.func.isRequired,
  onRetry: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
}
